import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProjectIcon from '../../ProjectIcon/ProjectIcon';
import ProjectVisual from '../ProjectVisual/ProjectVisual';
import PillLink from '../../ui/PillLink/PillLink';
import StatusPill from '../../ui/StatusPill/StatusPill';
import { linkHost, plainProjectName, primaryLink, type Project } from '../../../data/projects';
import { useProjectCopy } from '../../../hooks/useProjectCopy';
import { useProjectKeyFact } from '../../../hooks/useProjectKeyFact';
import { useLanguage } from '../../../i18n/useLanguage';
import styles from './ProjectCard.module.css';

type CardFormat = 'card' | 'wide';
export type CardMeta = 'links' | 'fact';

interface ProjectCardProps {
    project: Project;
    format?: CardFormat;
    meta?: CardMeta;
}

export default function ProjectCard({ project, format = 'card', meta = 'links' }: ProjectCardProps) {
    const { t } = useTranslation();
    const { localizedPath } = useLanguage();
    const copy = useProjectCopy()(project.slug);
    const keyFact = useProjectKeyFact()(project);
    const detailPath = localizedPath(`/work/${project.slug}`);
    const externalLink = primaryLink(project);
    const externalLabel = externalLink?.kind === 'website' ? linkHost(externalLink) : externalLink && t(`links.${externalLink.kind}`);
    const showsLinks = meta === 'links';

    return (
        <article className={styles.card} data-format={format}>
            <Link to={detailPath} className={styles.media} aria-label={t('project.open', { name: plainProjectName(project) })}>
                <ProjectVisual project={project} format={format} screenAlt={copy.screenAlt} />
                {showsLinks && (
                    <span className={styles.status}>
                        <StatusPill status={project.status} tone="onMedia" />
                    </span>
                )}
            </Link>
            <div className={styles.meta}>
                <ProjectIcon project={project} size="medium" />
                <div className={styles.text}>
                    <h3 className={styles.name}>
                        <Link to={detailPath}>{project.name}</Link>
                    </h3>
                    <p className={styles.category}>{copy.category}</p>
                </div>
                {showsLinks && externalLink && (
                    <PillLink href={externalLink.href} variant="outline" size="small" icon="arrowUpRight" className={styles.external}>
                        {externalLabel}
                    </PillLink>
                )}
                {!showsLinks && <p className={styles.fact}>{keyFact}</p>}
            </div>
        </article>
    );
}
